import useWidth from '@components/Hooks/useWidth';
import ObjectScreen from '@components/v2/layout/ObjectScreen/ObjectScreen';
import Breadcrumb from '@components/v2/ui/Breadcrumb/Breadcrumb';
import Loading from '@components/v2/ui/Loading/Loading';
import { Stack } from '@mui/material';
import LangEnum from '../../../../lang/LangEnum';
import { OrderObjectTestIds } from '../../../../utils/testIds/OrderObject.testIds';
import { OrderProductsTestIds } from '../../../../utils/testIds/OrderProducts.testIds';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import useHideContactBrandButton from '../../../context/ApplicationContext/hooks/useHideContactBrandButton';
import { ProductLockProvider } from '../../../products/products-details-v2/hooks/useProductLock';
import OrderRachelScopeProvider from '../../components/OrderRachelScopeProvider';
import { OrderContextProvider, useOrderContext } from '../../contexts/Order.context';
import { OrderActionsContextProvider } from '../../contexts/OrderActions.context';
import {
  OrderCalculateContextProvider,
  useOrderCalculateContext,
} from '../../contexts/OrderCalculate.context';
import {
  OrderConditionContextProvider,
  useOrderConditionContext,
} from '../../contexts/OrderCondition.context';
import {
  OrderEditSectionsContextProvider,
  useOrderEditSectionsContext,
} from '../../edit/sections/OrderEditSections.context';
import OrderSummaryEndActions from '../../edit/summary/components/actions/OrderSummaryEndActions';
import useOrderURLValidation from '../../hooks/useOrderURLValidation';
import OrderModals from '../../modals/OrderModals';
import { ORDER_OBJECT_TAB } from '../../orders.constants';
import { OrderView } from '../../ordersView.types';
import OrderConditionErrorsTooltipContent from './components/OrderConditionErrorsTooltipContent/OrderConditionErrorsTooltipContent';
import OrderErrorIcon from './components/OrderErrorIcon';
import OrderObjectDetailsSections from './components/OrderObjectDetailsSections/OrderObjectDetailsSections';
import OrderObjectOfflineBanner from './components/OrderObjectOfflineBanner/OrderObjectOfflineBanner';
import OrderObjectTabContent from './components/OrderObjectTabContent';
import OrderProductsErrorsTooltipContent from './components/OrderProductsErrorsTooltipContent';
import { useOrderDetailsRachelReveal } from './hooks/useOrderDetailsRachelReveal';
import useOrderObjectPage from './hooks/useOrderObjectPage';
import useOrderObjectTargetsTabSummary from './hooks/useOrderObjectTargetsTabSummary';
import useOrderTabsPolicyViolations from './hooks/useOrderTabsPolicyViolations';
import { OrderTabKey } from './orderObjectPage.types';
import { getConditionsTabEndIcon, getDetailsToggleAlert } from './orderObjectPage.utils';

interface OrderObjectPageProps {
  isOfflineMode?: boolean;
  onSuccessSyncOfflineOrder?: () => void;
}

const OrderObjectPageContent = ({ isOfflineMode }: OrderObjectPageProps) => {
  const { team } = useApplicationContext();
  const { order, canEditOrder } = useOrderContext();
  const { summaryTotals } = useOrderCalculateContext();
  const { isCustomizedTabletDown } = useWidth();
  const {
    breadcrumbItems,
    tags,
    productsErrors,
    productsBusinessPolicyViolations,
    hasProductsErrors,
  } = useOrderObjectPage({ order, isOfflineMode });
  const { openProductsPolicyViolations, openConditionsPolicyViolations } =
    useOrderTabsPolicyViolations();
  const { orderConditions } = useOrderConditionContext();
  const {
    hasConditionsErrors,
    rawConditionErrors,
    isValidatingOrderConditions,
    hasGeneralDataErrors,
    generalDataErrors,
  } = useOrderEditSectionsContext();
  const totals = summaryTotals?.calculate?.total;
  const targetsTab = useOrderObjectTargetsTabSummary({
    orderId: order?.id,
    status: order?.status,
  });

  useHideContactBrandButton();
  const { orderView } = useOrderURLValidation({
    canEditOrder,
    currentOrder: order,
    brandIntegrations: team?.brand?.integrations,
  });

  const isReadOnlyView = orderView === OrderView.FINISHED;
  const { isDetailsOpen, onDetailsOpenChange } = useOrderDetailsRachelReveal({
    generalDataKey: `${order?.salesRepresentative?.id}|${order?.priceTable?.id}`,
    isOpenByDefault: !isCustomizedTabletDown,
  });

  if (!order) {
    return (
      <Stack sx={{ height: '100%', alignItems: 'center', justifyContent: 'center' }}>
        <Loading />
      </Stack>
    );
  }

  return (
    <ObjectScreen
      objectId={order.id}
      objectType="order"
      defaultTab={ORDER_OBJECT_TAB.PRODUCTS}
      isDetailsOpen={isDetailsOpen}
      isDetailsPanelInlineOnTablet
      onDetailsOpenChange={onDetailsOpenChange}
      detailsToggleAlert={getDetailsToggleAlert({
        hasErrors: hasGeneralDataErrors,
        errors: generalDataErrors,
      })}
    >
      <ObjectScreen.Header
        tags={tags}
        actions={<OrderSummaryEndActions />}
        breadcrumb={<Breadcrumb items={breadcrumbItems} />}
      />

      {isOfflineMode && (
        <ObjectScreen.Slot name="afterHeader">
          <OrderObjectOfflineBanner order={order} />
        </ObjectScreen.Slot>
      )}

      <ObjectScreen.Tabs>
        {!isOfflineMode && (
          <ObjectScreen.Tab
            tabKey={ORDER_OBJECT_TAB.ACTIVITIES}
            label={LangEnum.ORDER_ACTIVITIES_TITLE}
            behavior="swap"
            testId={OrderObjectTestIds.TAB_ACTIVITIES}
          />
        )}
        {targetsTab.isVisible && (
          <ObjectScreen.Tab
            tabKey={ORDER_OBJECT_TAB.TARGETS}
            label={LangEnum.GLOBAL_PLANS}
            count={targetsTab.count}
            behavior="swap"
            testId={OrderObjectTestIds.TAB_TARGETS}
          />
        )}
        <ObjectScreen.Tab
          tabKey={ORDER_OBJECT_TAB.PRODUCTS}
          label={LangEnum.GLOBAL_PRODUCTS}
          count={totals?.quantity}
          behavior="swap"
          testId={OrderObjectTestIds.TAB_PRODUCTS}
          endIcon={
            hasProductsErrors ? (
              <OrderErrorIcon
                testId={OrderProductsTestIds.OBJECT_TAB_ERROR_ICON}
                onClick={openProductsPolicyViolations}
              />
            ) : undefined
          }
          tooltip={
            hasProductsErrors ? (
              <OrderProductsErrorsTooltipContent
                errors={productsErrors}
                policyViolations={productsBusinessPolicyViolations}
                onOpenDetails={openProductsPolicyViolations}
              />
            ) : undefined
          }
        />
        <ObjectScreen.Tab
          tabKey={ORDER_OBJECT_TAB.CONDITIONS}
          label={LangEnum.GLOBAL_CONDITIONS}
          count={orderConditions.length}
          behavior="swap"
          testId={OrderObjectTestIds.TAB_CONDITIONS}
          endIcon={getConditionsTabEndIcon({
            isValidating: isValidatingOrderConditions,
            hasErrors: hasConditionsErrors,
            onClick: openConditionsPolicyViolations,
          })}
          tooltip={
            hasConditionsErrors && !isValidatingOrderConditions ? (
              <OrderConditionErrorsTooltipContent
                errors={rawConditionErrors.flatMap(({ errors }) => errors)}
                onOpenDetails={openConditionsPolicyViolations}
              />
            ) : undefined
          }
        />
      </ObjectScreen.Tabs>

      <ObjectScreen.Content>
        {(activeTab) => (
          <OrderObjectTabContent
            activeTab={activeTab as OrderTabKey}
            order={order}
            canEditOrder={canEditOrder}
            isReadOnlyView={isReadOnlyView}
            isOfflineMode={isOfflineMode}
          />
        )}
      </ObjectScreen.Content>

      <ObjectScreen.DetailsPanel>
        <OrderObjectDetailsSections
          order={order}
          isReadOnlyView={isReadOnlyView}
          isOfflineMode={isOfflineMode}
        />
      </ObjectScreen.DetailsPanel>
    </ObjectScreen>
  );
};

const OrderObjectPage = ({ isOfflineMode, onSuccessSyncOfflineOrder }: OrderObjectPageProps) => (
  <OrderActionsContextProvider isOfflineMode={isOfflineMode}>
    <OrderContextProvider isOfflineMode={isOfflineMode}>
      <OrderCalculateContextProvider isOfflineMode={isOfflineMode}>
        <OrderModals
          isOfflineMode={isOfflineMode}
          onSuccessSyncOfflineOrder={onSuccessSyncOfflineOrder}
        />
        <OrderConditionContextProvider isOfflineMode={isOfflineMode}>
          <OrderEditSectionsContextProvider isOfflineMode={isOfflineMode}>
            <ProductLockProvider isEnabledOnTablet>
              <OrderRachelScopeProvider>
                <OrderObjectPageContent isOfflineMode={isOfflineMode} />
              </OrderRachelScopeProvider>
            </ProductLockProvider>
          </OrderEditSectionsContextProvider>
        </OrderConditionContextProvider>
      </OrderCalculateContextProvider>
    </OrderContextProvider>
  </OrderActionsContextProvider>
);

export default OrderObjectPage;
